import { api, isDemoMode } from "../api/client";
import type { Task } from "@nelurio/contracts";
import { fetchRoutines } from "./routines";
import { localDateKey } from "./dayCloseRecords";

// Records layer re-exported for existing consumers (DayClosePopup, ProgressPage).
export { localDateKey, readCloses, weekEfficiency, writeClose } from "./dayCloseRecords";
export type { DayCloseRecord } from "./dayCloseRecords";

export interface UnfinishedItem {
  id: string;
  title: string;
  kind: "task" | "routine" | "review";
  time: string | null;
}

/** Collect yesterday's open items (tasks + routines + reviews) for the summary. */
export async function collectUnfinished(dayKey: string): Promise<{ items: UnfinishedItem[]; total: number; done: number; ok: boolean }> {
  // `ok=false` means a fetch failed and the totals are INCOMPLETE — callers must
  // not treat the day as empty (the popup used to permanently mark it seen).
  let ok = true;
  const routineData = await fetchRoutines(dayKey).catch(() => { ok = false; return { routines: [] as import("./routines").Routine[], done: [] as string[] }; });
  const doneRoutines = routineData.done;
  const routines = routineData.routines.filter((routine) => routine.days.includes(new Date(`${dayKey}T12:00:00`).getDay()));
  const unfinishedRoutines = routines.filter((routine) => !doneRoutines.includes(routine.id))
    .map((routine) => ({ id: routine.id, title: routine.title, kind: "routine" as const, time: routine.time }));

  let tasks: Task[] = [];
  let reviewsDone = 0;
  let reviewsTotal = 0;
  if (!isDemoMode()) {
    try {
      tasks = await api<Task[]>(`/tasks?date=${dayKey}`);
      const dash = await api<{ reviews?: Array<{ id: string; completedAt?: string }> }>(`/dashboard?date=${dayKey}`);
      const reviews = dash.reviews ?? [];
      reviewsTotal = reviews.length;
      reviewsDone = reviews.filter((review) => review.completedAt).length;
    } catch { ok = false; /* offline — totals incomplete */ }
  }
  const unfinishedTasks = tasks
    .filter((task) => task.status !== "completed" && task.kind !== "exam" && task.kind !== "class" && task.kind !== "new_lesson")
    .map((task) => ({ id: task.id, title: task.title, kind: "task" as const, time: task.dueTime ?? null }));

  const total = tasks.filter((task) => task.kind !== "exam" && task.kind !== "class" && task.kind !== "new_lesson").length
    + routines.length + reviewsTotal;
  const done = tasks.filter((task) => task.status === "completed").length
    + (routines.length - unfinishedRoutines.length) + reviewsDone;

  return { items: [...unfinishedTasks, ...unfinishedRoutines], total, done, ok };
}

/** Move one unfinished task to tomorrow (server) — routines repeat anyway. */
export async function moveToTomorrow(item: UnfinishedItem, dayKey: string) {
  if (item.kind !== "task" || isDemoMode()) return;
  const tomorrow = new Date(`${dayKey}T12:00:00`);
  tomorrow.setDate(tomorrow.getDate() + 1);
  await api(`/tasks/${item.id}`, { method: "PATCH", body: JSON.stringify({ dueDate: localDateKey(tomorrow) }) });
}

/** Retro-active tick: "I did finish this that day, I just forgot to check it off." */
export async function markItemDone(item: UnfinishedItem, dayKey: string, done: boolean) {
  if (isDemoMode()) return;
  if (item.kind === "task") {
    await api(`/tasks/${item.id}`, { method: "PATCH", body: JSON.stringify({ status: done ? "completed" : "open" }) });
  } else if (item.kind === "routine") {
    const { toggleRoutineDoneApi } = await import("./routines");
    await toggleRoutineDoneApi(item.id, dayKey, done);
  }
}
